import {
  CloseOutlineIcon,
  DemostackingIcon,
  MouseActionIcon,
  MouseActionOffIcon,
  NotesIcon,
} from '@carboncopyco/icons';
import { Separator, ToggleButton, TooltipBubble } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { NotesScreen } from './NotesScreen';
import SpeakerPanelAssetsList from './SpeakerPanelAssetsList';
import { Stopwatch } from './Stopwatch';
import { WindowsScreen } from './WindowsScreen';

export type SpeakerRef = {
  getChildTime: () => number;
  ref: HTMLDivElement | null;
  isNotesOpen: boolean;
  isWindowsOpen: boolean;
  isMouseHighlightActive: boolean;
  newTime: (time: number) => void;
} | null;

export interface SpeakerViewItem {
  presentationName: string;
  image: string;
  notes?: string;
  id: number;
  screenshotImageType: 'tour' | 'overlay' | 'demo' | 'url';
  canDetach?: boolean;
}
export interface SpeakerViewScreenProps {
  activeItem: number;
  onClose: (time: number) => void;
  onDetach?: (index: number) => void;
  onToggleShowDemos?: (isOpen: boolean) => void;
  onToggleShowNotes?: (isOpen: boolean) => void;
  items: SpeakerViewItem[];
  onSetActive: (index: number) => void;
  onMouseHighlightClick: (isEnabled: boolean) => void;
  startTime?: number;
  shouldShowNotes?: boolean;
  shouldShowWindows?: boolean;
  shouldShowMouseHighlight?: boolean;
  noDrag?: {
    enabled: boolean;
    toggleEnabled: boolean;
  };
}
export const SpeakerViewScreen = forwardRef<SpeakerRef, SpeakerViewScreenProps>(
  (
    {
      activeItem,
      onClose,
      items,
      onMouseHighlightClick,
      startTime = 0,
      onSetActive,
      onDetach,
      onToggleShowDemos,
      onToggleShowNotes,
      shouldShowNotes = false,
      shouldShowWindows = false,
      shouldShowMouseHighlight = true,
      noDrag = {
        enabled: false,
        toggleEnabled: false,
      },
    },
    ref
  ) => {
    const [showNotes, { toggle: toggleShowNotes }] =
      useBoolean(shouldShowNotes);
    const [showDemos, { toggle: toggleShowDemos }] =
      useBoolean(shouldShowWindows);
    const [isMouseHighlightActive, setIsMouseHighlightActive] = useBoolean(
      shouldShowMouseHighlight
    );
    const [time, setTime] = useState(startTime);
    const refObject = useRef<HTMLDivElement>(null);
    const hasItems = items?.length > 0;
    const { trackEvent } = useAnalytics();
    const flags = useFlags();
    useImperativeHandle(
      ref,
      () => ({
        getChildTime: () => time,
        newTime: (newTime: number) => setTime(newTime),
        isWindowsOpen: showDemos,
        isNotesOpen: showNotes,
        isMouseHighlightActive,
        ref: refObject.current,
      }),
      [time, activeItem, showDemos, showNotes]
    );

    useEffect(() => {
      const event = showDemos
        ? AnalyticsTrackEventName.PRESENTATION_CLICK_SPEAKER_STACKING_ACTIVATE
        : AnalyticsTrackEventName.PRESENTATION_CLICK_SPEAKER_STACKING_DEACTIVATE;
      trackEvent(event, {
        location: 'speaker-view',
      });
    }, [showDemos]);

    useEffect(() => {
      const event = showNotes
        ? AnalyticsTrackEventName.PRESENTATION_CLICK_SPEAKER_NOTES_ACTIVATE
        : AnalyticsTrackEventName.PRESENTATION_CLICK_SPEAKER_NOTES_DEACTIVATE;
      trackEvent(event, {
        location: 'speaker-view',
      });
    }, [showNotes]);

    useEffect(() => {
      const event = isMouseHighlightActive
        ? AnalyticsTrackEventName.PRESENTATION_CLICK_SPEAKER_MOUSE_ANIMATION_ACTIVATE
        : AnalyticsTrackEventName.PRESENTATION_CLICK_SPEAKER_MOUSE_ANIMATION_DEACTIVATE;
      trackEvent(event, {
        location: 'speaker-view',
      });
    }, [isMouseHighlightActive]);

    return (
      <div
        className="bg-ds-natural-600 rounded-md"
        ref={refObject}
      >
        <div className="draggable flex w-[420px] h-12 gap-1.5">
          <div className="px-2.5">
            <Stopwatch
              value={time}
              onChange={setTime}
              shouldRunTimer={startTime === 0}
              noDrag={noDrag.enabled}
            />
          </div>
          <div>
            <Separator
              color="bg-ds-natural-500"
              middle={false}
            />
          </div>
          <div className="flex justify-center items-center flex-grow gap-1.5 h-full">
            <TooltipBubble
              placement="bottom"
              title="Toggle demos"
            >
              <ToggleButton
                ariaLabel="Toggle demos"
                dark
                selected={showDemos}
                icon={<DemostackingIcon size={20} />}
                onSelect={() => {
                  toggleShowDemos();
                  onToggleShowDemos?.(!showDemos);
                }}
                noDrag={noDrag.enabled}
              />
            </TooltipBubble>
            <TooltipBubble
              placement="bottom"
              title="Speaker notes"
            >
              <div>
                <ToggleButton
                  ariaLabel="Toggle notes"
                  dark
                  selected={showNotes}
                  icon={<NotesIcon size={20} />}
                  onSelect={() => {
                    toggleShowNotes();
                    onToggleShowNotes?.(!showNotes);
                  }}
                  noDrag={noDrag.enabled}
                />
              </div>
            </TooltipBubble>
            <TooltipBubble
              placement="bottom"
              title={
                !isMouseHighlightActive
                  ? 'Highlight cursor'
                  : 'Remove highlight'
              }
            >
              <ToggleButton
                ariaLabel="toggle mouse highlight"
                dark
                icon={
                  !isMouseHighlightActive ? (
                    <MouseActionOffIcon size={20} />
                  ) : (
                    <MouseActionIcon size={20} />
                  )
                }
                onSelect={() => {
                  setIsMouseHighlightActive.set(!isMouseHighlightActive);
                  onMouseHighlightClick(!isMouseHighlightActive);
                }}
                noDrag={noDrag.enabled}
              />
            </TooltipBubble>
          </div>
          <div>
            <Separator
              color="bg-ds-natural-500"
              middle={false}
            />
          </div>
          <div className="flex justify-self-center justify-center items-center pr-2">
            <TooltipBubble
              placement="bottom"
              title="Close speaker panel"
            >
              <ToggleButton
                ariaLabel="Close speaker panel"
                dark
                icon={<CloseOutlineIcon size={20} />}
                onSelect={() => onClose(time)}
                noDrag={noDrag.enabled}
              />
            </TooltipBubble>
          </div>
        </div>
        {showDemos && hasItems && (
          <>
            <Separator
              horizontal={true}
              color="bg-ds-natural-500"
              middle={false}
            />
            {flags.speakerPanelNew ? (
              <SpeakerPanelAssetsList
                items={items}
                onDetach={onDetach}
                activePresentation={activeItem}
                setActivePresentation={onSetActive}
              />
            ) : (
              <WindowsScreen
                items={items}
                activePresentation={activeItem}
                setActivePresentation={onSetActive}
              />
            )}
          </>
        )}
        {showNotes && hasItems && (
          <>
            <Separator
              horizontal={true}
              color="bg-ds-natural-500"
              middle={false}
            />
            <NotesScreen
              items={items}
              activeItemIndex={activeItem}
            />
          </>
        )}
      </div>
    );
  }
);

SpeakerViewScreen.displayName = 'SpeakerViewScreen';
